<!--
  Shared Starvly brand lockup — logomark + wordmark — used in web-ui's
  AppHeader, the kiosk topbar, the POS sidebar, and the customer-display
  banner. Define once, render everywhere so the brand stays in sync.

  Props:
    - size: 'sm' | 'md' | 'lg'  (default 'md')
    - wordmark: boolean         (default true; hide for tight layouts)
    - inverted: boolean         (default false; flips colours for dark backgrounds)
    - accent: CSS color         (override the dot/lower-arc accent — defaults
                                 to --starvly-brand-accent or #D94E1F)

  Styling: the SVG strokes use `currentColor` so the surrounding text
  colour controls the mark. The accent stroke uses a CSS custom property
  that consumers can override per-context (kiosk theme, dark mode, etc.).
-->
<template>
  <span
    class="starvly-brand"
    :class="[`size-${size}`, { 'is-inverted': inverted }]"
    :style="accent ? { '--starvly-brand-accent': accent } : null"
  >
    <svg
      class="starvly-mark"
      :width="markSize"
      :height="markSize"
      viewBox="0 0 32 32"
      aria-hidden="true"
    >
      <circle cx="16" cy="16" fill="none" opacity=".25" r="14" stroke="currentColor" stroke-width="1.4" />
      <path d="M8 12 A8 8 0 0 1 24 12" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2.6" />
      <path
        d="M24 20 A8 8 0 0 1 8 20"
        fill="none"
        stroke="var(--starvly-brand-accent, #D94E1F)"
        stroke-linecap="round"
        stroke-width="2.6"
      />
      <circle cx="16" cy="16" fill="var(--starvly-brand-accent, #D94E1F)" r="1.6" />
    </svg>
    <span v-if="wordmark" class="starvly-wordmark">
      starvly<span class="starvly-wordmark-dot">.</span>
    </span>
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  size: { type: String, default: 'md', validator: (v) => ['sm', 'md', 'lg'].includes(v) },
  wordmark: { type: Boolean, default: true },
  inverted: { type: Boolean, default: false },
  accent: { type: String, default: '' },
})

const markSize = computed(() => {
  return props.size === 'sm' ? 22 : props.size === 'lg' ? 34 : 28
})
</script>

<style scoped>
.starvly-brand {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: inherit;
  text-decoration: none;
  user-select: none;
}

.starvly-brand.is-inverted {
  color: #ffffff;
}

.starvly-mark {
  display: block;
  flex-shrink: 0;
}

.starvly-wordmark {
  font-family: 'Geist', 'Inter', system-ui, sans-serif;
  font-weight: 500;
  letter-spacing: -0.04em;
  text-transform: lowercase;
  line-height: 1;
}

.starvly-wordmark-dot {
  color: var(--starvly-brand-accent, #D94E1F);
}

.starvly-brand.size-sm .starvly-wordmark { font-size: 14px; }
.starvly-brand.size-md .starvly-wordmark { font-size: 18px; }
.starvly-brand.size-lg .starvly-wordmark { font-size: 24px; }
</style>
